// Contexto de permisos del design-system propio (reemplaza el PermissionsProvider/Gate que
// antes venían de @fiado/react-kit). UI pura: no conoce el catálogo de permisos, recibe los
// chequeos inyectados vía <PermissionsProvider value={...}> (lo alimenta PermissionsBridge desde
// useAuth). Gate y ActionMenu consumen usePermissions() para decidir qué renderizan.
import { createContext, useContext, type ReactNode } from 'react';

export interface PermissionsContextValue {
    hasPermission: (permission: string) => boolean;
    hasScopeAccess: (scope: string, scopeRef?: string) => boolean;
}

// Default deny-all: si algún consumidor queda fuera del provider, no filtra UI sensible.
const denyAll: PermissionsContextValue = {
    hasPermission: () => false,
    hasScopeAccess: () => false,
};

const PermissionsContext = createContext<PermissionsContextValue>(denyAll);

export function PermissionsProvider({
    value,
    children,
}: {
    value: PermissionsContextValue;
    children: ReactNode;
}) {
    return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions(): PermissionsContextValue {
    return useContext(PermissionsContext);
}

export interface GateProps {
    permission: string;
    scope?: string;
    scopeRef?: string;
    fallback?: ReactNode;
    children: ReactNode;
}

export function Gate({ permission, scope, scopeRef, fallback = null, children }: GateProps) {
    const { hasPermission, hasScopeAccess } = usePermissions();
    const allowed = hasPermission(permission) && (scope ? hasScopeAccess(scope, scopeRef) : true);
    return <>{allowed ? children : fallback}</>;
}
